/**
 * Ring-buffer logger.
 *
 * Default: silent. Each `log()` call re-checks debug state, so setting
 *
 *   localStorage.__pt_debug = '1'
 *
 * from the browser console enables console mirroring immediately for all
 * subsequent logs (no page reload needed). URL flag `?ptdebug=1` is captured
 * once at module load (URL doesn't change at runtime without navigation).
 */

export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
  t: number;
  lvl: LogLevel;
  msg: string;
  err?: unknown;
}

const MAX_ENTRIES = 50;
const RING: LogEntry[] = [];

let urlFlag = false;
try {
  urlFlag = typeof location !== 'undefined' && location.search.indexOf('ptdebug=1') !== -1;
} catch {
  /* sandboxed */
}

function isDebugMode(): boolean {
  if (urlFlag) return true;
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('__pt_debug') === '1';
  } catch {
    return false;
  }
}

export function log(lvl: LogLevel, msg: string, err?: unknown): void {
  const entry: LogEntry = { t: Date.now(), lvl, msg };
  if (err !== undefined) entry.err = err;
  RING.push(entry);
  if (RING.length > MAX_ENTRIES) RING.shift();

  if (isDebugMode()) {
    const sink = lvl === 'error' ? console.error : lvl === 'warn' ? console.warn : console.log;
    if (err !== undefined) sink('[pt-sdk:' + lvl + ']', msg, err);
    else sink('[pt-sdk:' + lvl + ']', msg);
  }
}

export function dumpLogs(): LogEntry[] {
  return RING.slice();
}

export function isDebug(): boolean {
  return isDebugMode();
}

/** Test-only: clear ring buffer. Not exported from index.ts so it tree-shakes. */
export function __clearLogsForTests(): void {
  RING.length = 0;
}
